import weatherSemantics from '@sharpr/types/mlb-weather-semantics.json';
import type { RoofClass, RoofState, RoofStateSource } from './types';

export interface GameRoofResolution {
  state: RoofState;
  source: RoofStateSource;
}

export interface MlbModelWindFlags {
  outToCf: number;
  inFromCf: number;
  outAny: number;
  inAny: number;
  leftToRight: number;
  rightToLeft: number;
}

const INDOOR_CONDITIONS = new Set(
  weatherSemantics.indoorConditions.map(condition => condition.toLowerCase()),
);
const { exactCenterHalfAngleDeg, radialAnyHalfAngleDeg } = weatherSemantics.modelWind;

function normalize360(degrees: number): number {
  const normalized = degrees % 360;
  return normalized < 0 ? normalized + 360 : normalized;
}

function signedAngleFromCf(windToDeg: number, cfBearingDeg: number): number {
  return ((normalize360(windToDeg) - normalize360(cfBearingDeg) + 540) % 360) - 180;
}

export function resolveGameRoofState(
  roof: RoofClass,
  officialCondition?: string | null,
): GameRoofResolution {
  if (roof === 'Fixed') return { state: 'closed', source: 'venue' };
  if (roof === 'Open') return { state: 'open', source: 'venue' };

  const condition = officialCondition?.trim().toLowerCase();
  if (!condition) return { state: 'unknown', source: 'unavailable' };
  return INDOOR_CONDITIONS.has(condition)
    ? { state: 'closed', source: 'mlb-official' }
    : { state: 'open', source: 'mlb-official' };
}

/**
 * Map Open-Meteo's continuous direction onto the eight sectors represented by
 * MLB's historical labels. Exact center is ±22.5°; broad out/in is ±67.5°.
 * This is deliberately separate from the coarser UI wind badge.
 */
export function classifyMlbModelWind(
  windFromDeg: number,
  cfBearingDeg: number,
  speedMph: number,
): MlbModelWindFlags {
  const empty: MlbModelWindFlags = {
    outToCf: 0,
    inFromCf: 0,
    outAny: 0,
    inAny: 0,
    leftToRight: 0,
    rightToLeft: 0,
  };
  if (
    !Number.isFinite(windFromDeg)
    || !Number.isFinite(cfBearingDeg)
    || !Number.isFinite(speedMph)
    || speedMph <= 0
  ) {
    return empty;
  }

  const windToDeg = normalize360(windFromDeg + 180);
  const signed = signedAngleFromCf(windToDeg, cfBearingDeg);
  const fromOutAxis = Math.abs(signed);
  const fromInAxis = 180 - fromOutAxis;
  const outAny = fromOutAxis <= radialAnyHalfAngleDeg;
  const inAny = fromInAxis <= radialAnyHalfAngleDeg;
  const crosswind = !outAny && !inAny;

  return {
    outToCf: fromOutAxis <= exactCenterHalfAngleDeg ? 1 : 0,
    inFromCf: fromInAxis <= exactCenterHalfAngleDeg ? 1 : 0,
    outAny: outAny ? 1 : 0,
    inAny: inAny ? 1 : 0,
    leftToRight: crosswind && signed > 0 ? 1 : 0,
    rightToLeft: crosswind && signed < 0 ? 1 : 0,
  };
}
